import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ListChecks, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiService } from '../../services/api';
import { NewPointVisibility, PointVisibility } from '../Home/PointVisibility';
import AttachMeeting from './AttachMeeting';

// Mes points à aborder : ceux que j'ai notés (privés ou publics), par client ou projet. Un point noté « de côté »
// pour un client remonte dans ses réunions ; on peut aussi le rattacher à une réunion précise de l'agenda.
const MyPoints = () => {
  const [points, setPoints] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [draft, setDraft] = useState('');
  const [scope, setScope] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => apiService.get('/talking-points/mine').then(setPoints)
    .catch((err) => { setError(err.message); setPoints([]); }), []);
  useEffect(() => {
    load();
    apiService.getProjectOptions().then((rows) => setProjects(rows || [])).catch(() => setProjects([]));
    apiService.get('/clients').then((rows) => setClients(rows || [])).catch(() => setClients([]));
  }, [load]);

  const groups = useMemo(() => {
    const byKey = new Map();
    (points || []).forEach((p) => {
      const key = p.project_id ? `p${p.project_id}` : p.client_id ? `c${p.client_id}` : 'meeting';
      const label = p.project_id ? `${p.project_name}${p.client_name ? ` · ${p.client_name}` : ''}`
        : p.client_id ? `${p.client_name} (tout le client)` : 'Pour une réunion';
      if (!byKey.has(key)) byKey.set(key, { key, label, points: [] });
      byKey.get(key).points.push(p);
    });
    return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [points]);

  const replace = (updated) => setPoints((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

  const add = async (event) => {
    event.preventDefault();
    if (!draft.trim() || !scope) return;
    setSaving(true);
    setError(null);
    const [kind, id] = [scope[0], Number(scope.slice(1))];
    try {
      const created = await apiService.post('/talking-points', {
        text: draft.trim(), visibility, ...(kind === 'p' ? { project_id: id } : { client_id: id }) });
      setPoints((prev) => [...(prev || []), created]);
      setDraft('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Abordé ou supprimé : le point quitte la liste des points en attente.
  const leave = async (point, request) => {
    try {
      await request();
      setPoints((prev) => prev.filter((p) => p.id !== point.id));
    } catch (err) {
      setError(err.message);
    }
  };
  const discuss = (point) => leave(point, () => apiService.patch(`/talking-points/${point.id}`, { status: 'discussed' }));
  const remove = (point) => leave(point, () => apiService.delete(`/talking-points/${point.id}`));
  const share = async (point, value) => {
    try {
      replace(await apiService.patch(`/talking-points/${point.id}`, { visibility: value }));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900"><ListChecks className="h-6 w-6 text-violet-500" /> Mes points à aborder</h1>
        <p className="text-sm text-slate-500">
          Note un point pour un client ou un projet sans attendre la réunion : il remontera dans ses prochaines réunions, ou rattache-le à une réunion précise.
        </p>
      </div>

      <form onSubmit={add} className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={300} placeholder="Point à aborder…"
          className="min-w-[12rem] flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-violet-100" />
        <select value={scope} onChange={(e) => setScope(e.target.value)}
          className="max-w-[16rem] rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700 outline-none">
          <option value="">Client ou projet…</option>
          <optgroup label="Clients">
            {clients.map((c) => <option key={`c${c.id}`} value={`c${c.id}`}>{c.name}</option>)}
          </optgroup>
          <optgroup label="Projets">
            {projects.map((p) => <option key={`p${p.id}`} value={`p${p.id}`}>{p.project_number ? `${p.project_number} · ` : ''}{p.name}</option>)}
          </optgroup>
        </select>
        <NewPointVisibility value={visibility} onChange={setVisibility} />
        <button type="submit" disabled={!draft.trim() || !scope || saving}
          className="flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-40">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Ajouter
        </button>
      </form>
      {error && <p className="text-sm text-rose-600">{error}</p>}

      {points === null ? (
        <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
      ) : groups.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400">Aucun point en attente.</p>
      ) : groups.map((group) => (
        <section key={group.key} className="rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
          <h2 className="mb-1.5 px-1 text-sm font-semibold text-slate-800">{group.label}</h2>
          <ul className="space-y-1">
            {group.points.map((point) => (
              <li key={point.id} className="group flex flex-wrap items-start gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50">
                <button type="button" title="Abordé" onClick={() => discuss(point)}
                  className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-500">
                  <Check size={11} />
                </button>
                <span className="min-w-0 flex-1 text-sm text-slate-800">{point.text}</span>
                <AttachMeeting point={point} onChange={replace} compact={!point.event_id} />
                <PointVisibility point={point} mine={point.mine} onChange={(value) => share(point, value)} />
                <button type="button" title="Supprimer" onClick={() => remove(point)} className="p-0.5 text-slate-300 opacity-0 hover:text-rose-500 group-hover:opacity-100">
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default MyPoints;
